import { enqueueSnackbar } from "notistack";
import { parseSocketResponse, useWebSocketEvent } from "@/contexts/websocket";
import type { WebSocketEventMap } from "@/contexts/websocket/types";
import type { QuickPanelMessage } from "../quickPanel.types";

// --------------------------------- TYPES ---------------------------------
type UseQuickPanelSocketEventParams<K extends keyof WebSocketEventMap> = {
	readonly event: K;
	readonly parse: (response: unknown, index: number) => QuickPanelMessage | null;
	readonly onMessages: (messages: QuickPanelMessage[]) => void;
	readonly enabled: boolean;
};

// -------------------------------- HELPERS --------------------------------
function enqueueQuickPanelSnackbar(message: QuickPanelMessage): void {
	const text = `${message.titulo}: ${message.mensagem}`;
	enqueueSnackbar(text, { variant: message.variant, key: message.id, autoHideDuration: 5000 });
}

// ---------------------------------- HOOK ----------------------------------
export function useQuickPanelSocketEvent<K extends keyof WebSocketEventMap>({
	event,
	parse,
	onMessages,
	enabled,
}: UseQuickPanelSocketEventParams<K>): void {
	useWebSocketEvent(
		event,
		(response, ...args) => {
			try {
				const parsedResponse = parseSocketResponse(response, args);
				const messages = (Array.isArray(parsedResponse) ? parsedResponse : [parsedResponse])
					.map((item, index) => parse(item, index))
					.filter((message): message is QuickPanelMessage => message !== null);

				if (messages.length === 0) return;

				onMessages(messages);
				messages.forEach(enqueueQuickPanelSnackbar);
			} catch (error) {
				console.error(`Falha ao processar evento Socket.IO "${event}".`, error);
			}
		},
		{ enabled },
	);
}
